import { cloneElement, createElement, Fragment, isValidElement } from "react"
import { escapeRegExp } from "library/safe-regexp.mjs"

/**
 * Default tag-to-component mapping.
 * Shorthand tags (b, i, u) map to their semantic HTML equivalents.
 */
const DEFAULT_COMPONENTS = {
    b: "strong",
    i: "em",
    u: "u",
    strong: "strong",
    em: "em",
}

/**
 * Matches an actual opening tag like `<b>` or `<link>`, but not `a < b`.
 * @type {RegExp}
 */
const HAS_TAGS = /<[a-z][a-z0-9]*>/i

/**
 * Parse a string containing simple rich-text tags (`<b>`, `<i>`, etc.)
 * into React elements using a component mapping.
 *
 * Auto-detects whether the string contains tags. If not, returns the
 * original string unchanged (zero overhead for plain text).
 *
 * @param {string} text - The string to parse, possibly containing tags
 * @param {Record<string, string|import("react").ReactElement>} [components] - Custom tag→component overrides merged on top of defaults
 * @returns {string|import("react").ReactNode}
 */
export function parseRichText(text, components) {
    if (typeof text !== "string" || !HAS_TAGS.test(text)) return text

    const merged = { ...DEFAULT_COMPONENTS, ...components }
    const parts = []
    let remaining = text
    let key = 0

    while (remaining) {
        const openMatch = remaining.match(/<([a-z][a-z0-9]*)>/i)
        if (!openMatch) {
            parts.push(remaining)
            break
        }

        const tag = openMatch[1].toLowerCase()
        const openIdx = openMatch.index
        const closeIdx = findMatchingClose(remaining, tag, openIdx + openMatch[0].length)

        if (closeIdx === -1) {
            parts.push(remaining)
            break
        }

        if (openIdx > 0) {
            parts.push(remaining.slice(0, openIdx))
        }

        const innerText = remaining.slice(openIdx + openMatch[0].length, closeIdx)
        const component = merged[tag]

        if (component) {
            const inner = parseRichText(innerText, components)
            if (isValidElement(component)) {
                parts.push(cloneElement(component, { key: key++ }, inner))
            } else {
                parts.push(createElement(component, { key: key++ }, inner))
            }
        } else {
            parts.push(innerText)
        }

        remaining = remaining.slice(closeIdx + tag.length + 3) // 3 = "</" + ">"
    }

    if (parts.length === 0) return text
    if (parts.length === 1 && typeof parts[0] === "string") return parts[0]

    return createElement(
        Fragment,
        null,
        ...parts.map(function wrapPart(p, i) {
            return typeof p === "string" ? createElement(Fragment, { key: `t${i}` }, p) : p
        })
    )
}

/**
 * Find the matching close tag for a given tag name, handling nested
 * same-name tags via a depth counter.  Case-insensitive so `<B>...</B>` works.
 *
 * @param {string} str - The string to search within
 * @param {string} tag - Lowercase tag name (e.g. "b")
 * @param {number} from - Start index (after the opening tag)
 * @returns {number} Index of the matching close tag, or -1
 */
function findMatchingClose(str, tag, from) {
    const safeTag = escapeRegExp(tag)
    // eslint-disable-next-line security/detect-non-literal-regexp -- tag name is escaped above
    const openRe = new RegExp(`<${safeTag}>`, "gi")
    // eslint-disable-next-line security/detect-non-literal-regexp -- tag name is escaped above
    const closeRe = new RegExp(`</${safeTag}>`, "gi")
    let depth = 1
    let pos = from

    while (pos < str.length && depth > 0) {
        openRe.lastIndex = pos
        closeRe.lastIndex = pos

        const nextOpen = openRe.exec(str)
        const nextClose = closeRe.exec(str)

        if (!nextClose) return -1

        if (nextOpen && nextOpen.index < nextClose.index) {
            depth++
            pos = nextOpen.index + nextOpen[0].length
        } else {
            depth--
            if (depth === 0) return nextClose.index
            pos = nextClose.index + nextClose[0].length
        }
    }

    return -1
}
